<template>
  <div
    :id="`tooltip_products_error_${collection._id}`"
    class="v2-collection-card"
    :class="{ 'v2-collection-card--error': isErrorInProducts }"
    @click="goToCollection"
  >
    <div class="v2-collection-card__image">
      <img
        :src="collection.logo || defaultCollectionImage"
        :alt="collection.name"
        class="v2-collection-card__img"
        @error="onImageError"
      >
    </div>
    <div
      v-if="isBrand && isEntityAdmin"
      class="v2-collection-card__actions"
      @click.stop
    >
      <v2-dropdown
        :options="brandDropdownActions"
        :placeholder="''"
        label-key="label"
        value-key="key"
        align="right"
        @select="onDropdownAction"
      >
        <template #toggle>
          <button class="v2-collection-card__menu-btn">
            <svg
              width="20"
              height="20"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="2"
            >
              <circle
                cx="12"
                cy="5"
                r="1"
              />
              <circle
                cx="12"
                cy="12"
                r="1"
              />
              <circle
                cx="12"
                cy="19"
                r="1"
              />
            </svg>
          </button>
        </template>
      </v2-dropdown>
    </div>
    <div class="v2-collection-card__body">
      <div class="v2-collection-card__name-row">
        <div
          class="v2-collection-card__name"
          :title="collection.name"
        >
          {{ collection.name }}
        </div>
        <!-- Retailers have always had a Download button here. KP-3919
             restyles it as an icon; with the flag off it stays the button it
             was, and the modal behind it falls back the same way. -->
        <template v-if="isRetailer">
          <download-icon-button
            v-if="isBrandCatalogueEnabled"
            :aria-label="`Download ${collection.name}`"
            @click="handleDownload(collection)"
          />
          <button
            v-else
            class="v2-collection-card__download-btn"
            @click.stop="handleDownload(collection)"
          >
            Download
          </button>
        </template>
      </div>
      <div class="v2-collection-card__count v2-mt-1">
        {{ collection.productsCount || 0 }} Products
      </div>
      <v2-badge
        v-if="collection.type && !isPreviousCollection"
        variant="default"
        class="v2-mt-1"
      >
        {{ getCollectionTypeText(collection.type) }}
      </v2-badge>
      <v2-badge
        v-if="isPreviousCollection"
        variant="destructive"
        class="v2-mt-1"
      >
        Deadline Closed
      </v2-badge>
      <div class="v2-collection-card__date v2-mt-1">
        {{ parseCollectionDate(collection.endDate) }}
      </div>
      <div
        v-if="collection.expectedDeliveryWindow && collection.expectedDeliveryWindow.fromDate && collection.expectedDeliveryWindow.toDate"
        class="v2-collection-card__meta"
      >
        Delivery window: {{ getMonthAndYearFromDate(collection.expectedDeliveryWindow.fromDate) }} - {{ getMonthAndYearFromDate(collection.expectedDeliveryWindow.toDate) }}
      </div>
      <div class="v2-collection-card__description v2-mt-1">
        <span :class="{ 'v2-collection-card__description--truncated': !readMore }">
          Description: {{ getDescription(collection.description) }}
        </span>
        <span
          v-if="collection.description && collection.description.length > DESCRIPTION_MAX_SHOWING_LENGTH"
          class="v2-collection-card__read-more"
          @click.stop="readMore = !readMore"
        >
          Read {{ readMore ? 'Less' : 'More' }}
        </span>
      </div>
      <div class="v2-collection-card__meta">
        Updated: {{ formattedDate(collection.updatedAt) }}
      </div>
    </div>
    <div
      v-if="isErrorInProducts"
      class="v2-collection-card__error-tooltip"
    >
      {{ PRODUCTS_ERROR_MESSAGE }}
    </div>
  </div>
</template>

<script>
import {
  parseCollectionDate,
  getCollectionTypeText,
  getDefaultProductImage,
} from '@/@core/utils/utils'
import { utils } from '@kingpin-global/kingpin-utils-frontend'
import { useCollectionCard } from '@/composables/useCollectionCard'
import V2Badge from '@/components/v2/V2Badge.vue'
import V2Dropdown from '@/components/v2/V2Dropdown.vue'
import DownloadIconButton from '@/components/collection/DownloadIconButton.vue'
import { useBrandCatalogueFlag } from '@/composables/useBrandCatalogueFlag'
import UserRoleMixin from '../../apps/UserRoleMixin.vue'

const { formattedDate } = utils

export default {
  name: 'V2CollectionCard',
  components: {
    DownloadIconButton,
    V2Badge,
    V2Dropdown,
  },
  mixins: [UserRoleMixin],
  props: {
    collection: {
      type: Object,
      default: () => ({}),
    },
    isPreviousCollection: {
      type: Boolean,
      default: false,
    },
    fetchCollections: {
      type: Function,
      default() {},
    },
  },
  setup(props) {
    const composable = useCollectionCard(props)
    return {
      ...composable,
      ...useBrandCatalogueFlag(),
    }
  },
  data() {
    return {
      defaultCollectionImage: getDefaultProductImage(300, 300),
      formattedDate,
      parseCollectionDate,
      getCollectionTypeText,
    }
  },
  computed: {
    isErrorInProducts() {
      return this.isBrand && this.collection.productsCount === 0
    },
    changeStatusLabel() {
      return this.isCollectionLive(this.collection.status)
        ? `Change to ${this.COLLECTION_STATUS.EXPIRED.LABEL}?`
        : `Change to ${this.COLLECTION_STATUS.LIVE.LABEL}?`
    },
    brandDropdownActions() {
      const actions = [
        { key: 'edit', label: 'Edit Collection' },
        { key: 'update-availability', label: 'Update Availability' },
        { key: 'delete', label: 'Delete Collection' },
        { key: 'download', label: 'Download Collection' },
        { key: 'copy-link', label: "Copy Collection's Link" },
        { key: 'change-status', label: this.changeStatusLabel, variant: this.isCollectionLive(this.collection.status) ? 'destructive' : 'success' },
        { key: 'price-books', label: 'Price Books' },
        { key: 'currency-books', label: 'Currency Books' },
      ]
      if (this.hasTransactionCurrencyFeature) {
        actions.push({ key: 'segmentation-books', label: 'Segmentation Books' })
      }
      return actions
    },
  },
  methods: {
    onImageError(event) {
      event.target.src = this.defaultCollectionImage
    },
    goToCollection() {
      this.$router.push({
        name: 'v2-collection/products',
        params: { collectionId: this.collection._id },
      })
    },
    handleEdit(collection) {
      this.beforeEdit(collection)
      this.$router.push({
        name: 'v2-collection/edit',
        params: { id: collection._id },
      })
    },
    handleUpdateAvailability(collection) {
      this.showUpdateAvailabilityModal(collection)
      this.$emit('show-modal', collection)
    },
    handleDownload(collection) {
      this.handleDownloadCollection(collection, this.isErrorInProducts, id => {
        this.$emit('show-download-modal', id)
      })
    },
    handleChangeStatus(collection) {
      this.updateCollectionStatus(collection, collection.status, this.fetchCollections)
    },
    _buildDropdownActionMap(collection) {
      return {
        edit: () => this.handleEdit(collection),
        'update-availability': () => this.handleUpdateAvailability(collection),
        delete: () => this.$emit('show-delete-modal', collection),
        download: () => this.handleDownload(collection),
        'copy-link': () => this.copyCollectionLink(collection),
        'change-status': () => this.handleChangeStatus(collection),
        'price-books': () => this.$emit('show-price-books', collection),
        'currency-books': () => this.$emit('show-currency-books', collection),
        'segmentation-books': () => this.$emit('show-segmentation-books', collection),
      }
    },
    onDropdownAction(actionKey) {
      const handler = this._buildDropdownActionMap(this.collection)[actionKey]
      if (handler) handler()
    },
  },
}
</script>

<style lang="scss" scoped>
@import '@/assets/scss/v2/_glass-mixins.scss';

.v2-collection-card {
  // Canonical no-backdrop-filter glass — see _glass-mixins.scss + note #3
  // in glassmorphism-migration-plan.md. Card grids render many tiles at
  // once; sheen + page substrate handle the visual without per-card blur.
  @include v2-glass-panel-flat;
  position: relative;
  max-width: 290px;
  cursor: pointer;
  // `contain: layout` only — `paint` would clip the kebab popover and
  // any other absolute descendants extending past the card box.
  contain: layout;
  transition: box-shadow 0.2s ease, transform 0.2s ease, border-color 0.2s ease;

  &:hover {
    border-color: var(--v2-glass-stroke-strong);
    transform: translateY(-1px);
    z-index: 1;
  }

  &--error {
    border-color: var(--v2-destructive);
  }

  &__image {
    position: relative;
    max-width: 290px;
    max-height: 290px;
    aspect-ratio: 1 / 1;
    overflow: hidden;
    background: var(--v2-muted);
    border-top-left-radius: var(--v2-radius-lg);
    border-top-right-radius: var(--v2-radius-lg);
  }

  &__img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }

  &__actions {
    position: absolute;
    top: var(--v2-space-2);
    right: var(--v2-space-2);
    z-index: 5;
  }

  &__menu-btn {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 32px;
    height: 32px;
    // Near-opaque floating fill — kebab sits over potentially dark
    // collection-cover images. No `backdrop-filter` because card grids
    // multiply the kebab N times; per-button blur was significant
    // compositing pressure during scroll. Floating surface is already
    // ~92% opaque so legibility is fine without blur.
    background: var(--v2-glass-surface-floating);
    border: 1px solid var(--v2-glass-stroke);
    border-radius: var(--v2-radius-md);
    cursor: pointer;
    color: var(--v2-foreground);

    &:hover {
      border-color: var(--v2-glass-stroke-strong);
    }
  }

  &__body {
    padding: var(--v2-space-3) var(--v2-space-4);
  }

  &__name-row {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: var(--v2-space-2);
  }

  // KP-3919: quiet indigo icon tile; solid on approach, "Download" tooltip
  // after a deliberate two-second hover. The text button below is the
  // pre-KP-3919 one, still shown while the flag is off.
  &__download-btn {
    flex-shrink: 0;
    padding: var(--v2-space-1) var(--v2-space-3);
    font-family: var(--v2-font-sans);
    font-size: 0.75rem;
    font-weight: 500;
    color: var(--v2-foreground);
    background: transparent;
    border: 1px solid var(--v2-border);
    border-radius: var(--v2-radius-md);
    cursor: pointer;
    transition: all 0.15s ease;

    &:hover {
      background: var(--v2-muted);
    }
  }

  &__name {
    font-family: var(--v2-font-sans);
    font-size: 0.875rem;
    font-weight: 600;
    color: var(--v2-foreground);
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    max-width: 200px;
  }

  &__count {
    font-size: 0.75rem;
    color: var(--v2-muted-foreground);
  }

  &__date {
    font-size: 0.75rem;
    color: var(--v2-muted-foreground);
  }

  &__meta {
    font-size: 0.75rem;
    color: var(--v2-muted-foreground);
    margin-top: var(--v2-space-0-5);
  }

  &__description {
    font-size: 0.75rem;
    color: var(--v2-muted-foreground);

    &--truncated {
      display: -webkit-box;
      -webkit-line-clamp: 2;
      -webkit-box-orient: vertical;
      overflow: hidden;
    }
  }

  &__read-more {
    font-size: 0.75rem;
    color: var(--v2-primary);
    cursor: pointer;
    font-weight: 500;

    &:hover {
      text-decoration: underline;
    }
  }

  &__error-tooltip {
    position: absolute;
    bottom: 100%;
    left: 50%;
    transform: translateX(-50%);
    background: var(--v2-destructive);
    color: #fff;
    font-size: 0.75rem;
    padding: var(--v2-space-2) var(--v2-space-3);
    border-radius: var(--v2-radius-md);
    width: max-content;
    max-width: 280px;
    text-align: center;
    line-height: 1.4;
    pointer-events: none;
    opacity: 0;
    transition: opacity 0.2s ease;
    margin-bottom: var(--v2-space-1);
    z-index: 10;
  }

  &--error:hover &__error-tooltip {
    opacity: 1;
  }
}

</style>
